import Button from '@atoms/Button/Button';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import { IMiniBasketResponse } from '@models/IMiniBasket';
import { IPromoCartItem } from '@models/IPromoBanner';
import ErrorBannerNotificationEnum from '@models/enums/ErrorBannerNotificationEnum';
import MiniBasketItem from '@molecules/MiniBasketItem/MiniBasketItem';
import PromoCartItem from '@molecules/PromoCartItem/PromoCartItem';
import { removeItemFromCart } from '@services/cart';
import useCartSizeStore from '@state/CartSizeStore';
import useErrorNotificationStore from '@state/ErrorBannerNotificationDisplayStore';
import updateDataLayer from '@utils/helpers/gtm';
import axios from 'axios';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';

interface Props {
  readonly miniBasketEndpoint: string;
  readonly miniBasketTitle: string;
}

const MiniBasket = ({ miniBasketEndpoint, miniBasketTitle }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [basketPayload, setBasketPayload] =
    useState<IMiniBasketResponse | null>(null);
  const [promoCartItem, setPromoCartItem] = useState<IPromoCartItem>();
  const { cartSizeName, setCartSize } = useCartSizeStore((state) => ({
    cartSizeName: state.cartSizeName,
    setCartSize: state.setCartSize,
  }));

  const { setErrorBannerNotification } = useErrorNotificationStore();

  const {
    basketLink,
    checkoutLink,
    cartItems = [],
    emptyMiniBasketLabel,
    giftItemNotification,
    includingTaxLabel,
    removeItemFromCartEndpoint,
    subtotal,
    trackingData,
    total,
    totalBasketValue,
    totalLabel,
  } = basketPayload ?? {};

  const hasBasketItems = cartItems.length > 0;
  const basketItemsCount = cartItems.reduce(
    (acc, item) => acc + item.selectedQuantity,
    0
  );
  const isDiscounted = total !== subtotal;

  const handleRemoveItemFromCart = async (code: string, isGift?: boolean) => {
    setIsLoading(true);

    try {
      const basketResponsePayload = await removeItemFromCart(
        removeItemFromCartEndpoint!,
        code,
        isGift
      );

      setBasketPayload({
        ...basketPayload!,
        cartItems: basketResponsePayload.basket.cartItems,
        total: basketResponsePayload.basket.orderSummary.total,
        totalBasketValue: basketResponsePayload.basket.orderSummary.totalValue,
        subtotal: basketResponsePayload.basket.orderSummary.subtotal,
        trackingData: basketResponsePayload.basket.trackingData,
      });

      const newPromoCartItem = basketResponsePayload.basket.promoBanner
        ?.isDiscountApplied
        ? basketResponsePayload.basket.promoBanner?.promoCartItem
        : undefined;
      setPromoCartItem(newPromoCartItem);
      setCartSize(Number(Cookies.get(cartSizeName)) || 0);

      const currentProduct = (trackingData?.products || []).find(
        (product) => product.item_id === code
      );

      if (currentProduct)
        updateDataLayer({
          event: 'removeFromCart',
          ecommerce: {
            currency: basketResponsePayload.basket.trackingData?.currencyCode,
            items: [{ ...currentProduct, quantity: 0 }],
            value: basketResponsePayload.basket.orderSummary.totalValue,
            quantityChange: -currentProduct.quantity,
          },
        });
    } catch {
      setErrorBannerNotification(ErrorBannerNotificationEnum.CartRemoveItem);
    } finally {
      setIsLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    const getMiniBasketDetails = async () => {
      try {
        setIsLoading(true);

        const { data } =
          await axios.get<IMiniBasketResponse>(miniBasketEndpoint);

        setPromoCartItem(data.promoCartItem);
        setBasketPayload(data);
      } catch {
        setErrorBannerNotification(
          ErrorBannerNotificationEnum.MiniBasketDetails
        );
      } finally {
        setIsLoading(false);
      }
    };

    getMiniBasketDetails();
  }, [miniBasketEndpoint]);

  return (
    <div
      className={classNames(
        'relative w-[488px] overflow-hidden rounded-[10px] bg-grey-100'
      )}
      data-testid="CT-157-MiniBasket"
    >
      <p
        className="h6 flex w-full items-center p-5"
        data-testid="CT-157-MiniBasket-Title"
      >
        {miniBasketTitle}

        {hasBasketItems && (
          <>
            &nbsp;
            <span
              className="body-two text-grey-450"
              data-testid="CT-157-MiniBasket-Items-Count"
            >
              ({basketItemsCount})
            </span>
          </>
        )}
      </p>

      {isLoading && (
        <div
          className={classNames(
            'absolute top-0 z-100 flex h-full w-full items-center justify-center',
            { 'bg-black-100/10': basketPayload }
          )}
        >
          <LoadingSpinner
            pulseItemClasses="bg-black h-5 w-5"
            data-testid="CT-157-MiniBasket-Loading-Spinner"
          />
        </div>
      )}

      {!basketPayload && <div className="min-h-[245px]" />}

      <AnimatePresence>
        {basketPayload && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex max-h-[510px] w-full flex-col"
            data-testid="CT-157-MiniBasket-Container"
          >
            {hasBasketItems ? (
              <>
                <div className="flex flex-shrink flex-col gap-5 overflow-auto px-5 pb-5">
                  {cartItems.map((item, index) => (
                    <MiniBasketItem
                      {...item}
                      giftItemNotification={giftItemNotification}
                      key={item.code}
                      onRemoveItemFromCart={handleRemoveItemFromCart}
                      dataTestId={`CT-157-MiniBasket-Item-${index}`}
                    />
                  ))}

                  {promoCartItem && <PromoCartItem {...promoCartItem} />}
                </div>

                <div
                  className="w-full bg-black-100 p-5 dark:bg-white"
                  data-testid="CT-157-MiniBasket-Bottom-Bar"
                >
                  <p
                    className="body-three text-grey dark:text-black-100"
                    data-testid="CT-157-MiniBasket-Subtotal-Label"
                  >
                    {totalLabel}{' '}
                    <span className="text-grey-150 dark:text-grey-450">
                      {includingTaxLabel}
                    </span>
                  </p>

                  <div
                    className={classNames(
                      'flex gap-y-5',
                      isDiscounted
                        ? 'flex-col'
                        : 'flex-wrap items-center justify-between gap-x-7.5'
                    )}
                  >
                    {isDiscounted ? (
                      <p
                        className="body-one body-one--bold text-yellow dark:text-black-100"
                        data-testid="CT-157-MiniBasket-Subtotal-Value"
                      >
                        {total}{' '}
                        <span className="body-two text-grey-200 line-through dark:text-grey-500">
                          {subtotal}
                        </span>
                      </p>
                    ) : (
                      <p
                        className="body-one body-one--bold text-white dark:text-black-100"
                        data-testid="CT-157-MiniBasket-Subtotal-Value"
                      >
                        {total}
                      </p>
                    )}

                    <div
                      className={classNames('flex flex-wrap gap-4', {
                        'w-full justify-between': isDiscounted,
                      })}
                    >
                      <Button
                        link={basketLink}
                        linkCallback={() => {
                          updateDataLayer({
                            event: 'ecommerceInteractions',
                            link_text: basketLink?.name,
                          });
                        }}
                        secondary
                        isLightMode
                        className={classNames(
                          'text-center text-white dark:text-black',
                          { 'flex-grow': isDiscounted }
                        )}
                        dataTestId="CT-157-MiniBasket-Basket-Link"
                      />

                      <Button
                        link={checkoutLink}
                        linkCallback={() => {
                          updateDataLayer({
                            event: 'beginCheckout',
                            ecommerce: {
                              currency: trackingData?.currencyCode,
                              items: trackingData?.products,
                              value: totalBasketValue,
                            },
                          });
                        }}
                        className={classNames('text-center', {
                          'flex-grow': isDiscounted,
                        })}
                        dataTestId="CT-157-MiniBasket-Checkout-Link"
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex min-h-[245px] w-full items-center justify-center">
                <p
                  className="body-one mb-12 text-grey-450"
                  data-testid="CT-157-MiniBasket-Empty-Basket-Label"
                >
                  {emptyMiniBasketLabel}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MiniBasket;
